import {
    Chart,
    ChartConfiguration,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
    Legend,
    Title,
    Filler,
    ChartData,
    ChartOptions,
    Point,
    TimeScaleOptions
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { loadAllCoinsData, displayLatestUpdate, dataUrl } from './modules/data.loader';
import { AvailableCoinsComponent } from './modules/available-coins.component';
import { router } from './modules/router';
import { initShareButton } from './modules/share-component';

// Register Chart.js components
Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
    Legend,
    Title,
    Filler
);

interface DcaSettings {
    purchaseAmount: number;
    repeatPurchase: 'weekly' | 'monthly' | 'quarterly';
    accumulateFor: number; // Months of payments
    startingFrom: number; // Months ago of the first payment
    startingDate: Date;
    taxRate: number; // Yearly tax on unrealized gains as a fraction (0 = disabled)
}

interface DcaResult {
    totalInvested: number;
    totalValue: number;
    percentChange: number;
    coinAmount: number;
    totalTax: number;
    valueWithoutTax: number;
    // Tax is paid on Dec 31 by selling coins
    taxEvents: Array<{
        date: Date;
        tax: number;
        coinsSold: number;
    }>;
    startDate: Date;
    endDate: Date;
    purchaseHistory: Array<{
        date: Date;
        price: number;
        investment: number;
        coinAmount: number;
    }>;
    postAccumulationHistory: Array<{
        date: Date;
        price: number;
    }>;
}

type TimeDataPoint = {
    x: Date;
    y: number;
};

const coinId_default = 'bitcoin';
const purchaseFrequency_default = 'monthly';
const purchaseAmount_default = 100;
const accumulateFor_default = 24;
const startingFrom_default = 24;
const PERIOD_MAX_MONTHS = 120; // How far back the investment period slider goes

let coinId = coinId_default;
let purchaseFrequency = purchaseFrequency_default;
let purchaseAmount = purchaseAmount_default;
let accumulateFor = accumulateFor_default;
let startingFrom = startingFrom_default;

// Payment dates from the starting date, one per period, until accumulation ends
function getInvestmentDates(settings: DcaSettings): Date[] {
    const accumulationEndDate = new Date(settings.startingDate);
    accumulationEndDate.setMonth(accumulationEndDate.getMonth() + settings.accumulateFor);

    const dates: Date[] = [];
    const current = new Date(settings.startingDate);
    while (current < accumulationEndDate) { // Use '<' to exclude the end date boundary
        dates.push(new Date(current));
        switch (settings.repeatPurchase.toLowerCase()) {
            case 'weekly':
                current.setDate(current.getDate() + 7);
                break;
            case 'monthly':
                current.setMonth(current.getMonth() + 1);
                break;
            case 'quarterly':
                current.setMonth(current.getMonth() + 3);
                break;
        }
    }
    return dates;
}

class DcaCalculator {
    private chart: Chart<'line', TimeDataPoint[]> | null = null;
    private selectedCoinId: string = coinId_default;
    private coin_markets: any[] = [];
    private updateUrlCallback: () => void;

    constructor(updateUrlCallback: () => void) {
        this.updateUrlCallback = updateUrlCallback;
        this.initializeChart();
        this.attachEventListeners();
    }

    private initializeChart() {
        const canvas = document.getElementById('dcaChart') as HTMLCanvasElement;
        if (!canvas) return;

        const now = new Date();
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(now.getFullYear() - 1);
        const twoYearsAgo = new Date(now);
        twoYearsAgo.setFullYear(now.getFullYear() - 2);

        const initialData: TimeDataPoint[] = [
            { x: twoYearsAgo, y: 0 },
            { x: oneYearAgo, y: 0 },
            { x: now, y: 0 }
        ];

        const config: ChartConfiguration<'line', TimeDataPoint[]> = {
            type: 'line',
            data: {
                datasets: [{
                    label: 'Portfolio Value',
                    data: initialData,
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    fill: true,
                    cubicInterpolationMode: 'monotone' // Smooth without overshooting the real values
                }, {
                    label: 'Total Invested',
                    data: initialData,
                    borderColor: '#007bff',
                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    fill: true,
                    cubicInterpolationMode: 'monotone'
                }, {
                    label: 'Value Without Tax',
                    data: [],
                    borderColor: '#6c757d',
                    borderDash: [6, 4],
                    borderWidth: 1.5,
                    pointRadius: 0,
                    fill: false,
                    cubicInterpolationMode: 'monotone'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true, text: 'Portfolio Value vs Total Invested'
                    },
                    legend: {
                        labels: {
                            // Hide datasets with no data (the no-tax line when tax is disabled)
                            filter: (item, data) => data.datasets[item.datasetIndex!].data.length > 0
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD'
                                    }).format(context.parsed.y);
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: 'month',
                            displayFormats: {
                                month: 'MMM yyyy'
                            }
                        },
                        title: { display: true, text: 'Date' },
                        ticks: {
                            source: 'auto',
                            maxRotation: 45,
                            minRotation: 45
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Amount ($)' },
                        ticks: {
                            callback: function(value) {
                                if (typeof value === 'number') {
                                    return new Intl.NumberFormat('en-US', {
                                        style: 'currency', currency: 'USD',
                                        minimumFractionDigits: 0, maximumFractionDigits: 0
                                    }).format(value);
                                }
                                return '';
                            }
                        }
                    }
                }
            }
        };
        this.chart = new Chart(canvas, config);
    }

    private attachEventListeners() {
        const periodStart = document.getElementById('periodStart') as HTMLInputElement;
        const periodEnd = document.getElementById('periodEnd') as HTMLInputElement;

        // Keep at least one month between the handles, moving back the one being dragged
        const onPeriodInput = (moved: HTMLInputElement) => {
            if (Number(periodEnd.value) - Number(periodStart.value) < 1) {
                if (moved === periodStart) {
                    periodStart.value = (Number(periodEnd.value) - 1).toString();
                } else {
                    periodEnd.value = (Number(periodStart.value) + 1).toString();
                }
            }
            this.updatePeriodSummary();
        };
        [periodStart, periodEnd].forEach(input => {
            input.addEventListener('input', () => onPeriodInput(input));
            // Recalculate only when the handle is released
            input.addEventListener('change', () => this.updateUrlCallback());
        });
        this.updatePeriodSummary();

        // Enable the tax rate input only when the tax is enabled
        const taxEnabled = document.getElementById('taxEnabled') as HTMLInputElement;
        const taxRate = document.getElementById('taxRate') as HTMLInputElement;
        taxEnabled.addEventListener('change', () => {
            taxRate.disabled = !taxEnabled.checked;
        });

        // Handle other inputs
        const otherInputs = ['purchaseAmount', 'repeatPurchase', 'taxEnabled', 'taxRate'];
        otherInputs.forEach(id => {
            const element = document.getElementById(id);
            if (element) {
                element.addEventListener('change', () => {
                    // Let the URL change trigger the update
                    this.updateUrlCallback();
                });
            }
        });
    }

    public getSettings(): DcaSettings {
        const purchaseAmount = document.getElementById('purchaseAmount') as HTMLInputElement;
        const repeatPurchase = document.getElementById('repeatPurchase') as HTMLSelectElement;
        const periodStart = document.getElementById('periodStart') as HTMLInputElement;
        const periodEnd = document.getElementById('periodEnd') as HTMLInputElement;
        const taxEnabled = document.getElementById('taxEnabled') as HTMLInputElement;
        const taxRate = document.getElementById('taxRate') as HTMLInputElement;
        const taxPercent = Math.min(Math.max(Number(taxRate.value) || 0, 0), 100);

        // Slider positions count months from PERIOD_MAX_MONTHS ago (0) to now (PERIOD_MAX_MONTHS)
        const startingFrom = PERIOD_MAX_MONTHS - Number(periodStart.value);
        const startingDate = new Date();
        startingDate.setMonth(startingDate.getMonth() - startingFrom);

        return {
            purchaseAmount: Number(purchaseAmount.value),
            repeatPurchase: repeatPurchase.value as 'weekly' | 'monthly' | 'quarterly',
            accumulateFor: Number(periodEnd.value) - Number(periodStart.value),
            startingFrom,
            startingDate,
            taxRate: taxEnabled.checked ? taxPercent / 100 : 0
        };
    }

    public updateSettings(settings: {
        coinId?: string;
        purchaseAmount?: number;
        repeatPurchase?: 'weekly' | 'monthly' | 'quarterly';
        accumulateFor?: number;
        startingFrom?: number;
        taxPercent?: number;
    }) {
        if (settings.coinId) {
            this.selectedCoinId = settings.coinId;
        }
        if (settings.taxPercent !== undefined) {
            const taxEnabled = document.getElementById('taxEnabled') as HTMLInputElement;
            const taxRate = document.getElementById('taxRate') as HTMLInputElement;
            if (taxEnabled && taxRate) {
                taxEnabled.checked = true;
                taxRate.disabled = false;
                taxRate.value = settings.taxPercent.toString();
            }
        }
        if (settings.purchaseAmount) {
            const input = document.getElementById('purchaseAmount') as HTMLInputElement;
            if (input) input.value = settings.purchaseAmount.toString();
        }
        if (settings.repeatPurchase) {
            const select = document.getElementById('repeatPurchase') as HTMLSelectElement;
            if (select) select.value = settings.repeatPurchase;
        }
        if (settings.startingFrom || settings.accumulateFor) {
            const current = this.getSettings();
            const clamp = (value: number, min: number, max: number) => Math.min(Math.max(Math.round(value), min), max);
            // Accumulation can't last longer than the time since the first payment
            const startingFrom = clamp(settings.startingFrom || current.startingFrom, 1, PERIOD_MAX_MONTHS);
            const accumulateFor = clamp(settings.accumulateFor || current.accumulateFor, 1, startingFrom);

            const periodStart = document.getElementById('periodStart') as HTMLInputElement;
            const periodEnd = document.getElementById('periodEnd') as HTMLInputElement;
            periodStart.value = (PERIOD_MAX_MONTHS - startingFrom).toString();
            periodEnd.value = (PERIOD_MAX_MONTHS - startingFrom + accumulateFor).toString();
            this.updatePeriodSummary();
        }
    }

    // Show the selected period on the slider track, its payment dates and the number of payments
    private updatePeriodSummary() {
        const periodStart = document.getElementById('periodStart') as HTMLInputElement;
        const periodEnd = document.getElementById('periodEnd') as HTMLInputElement;
        const periodFill = document.getElementById('periodFill');
        const periodDates = document.getElementById('periodDates');
        const paymentCount = document.getElementById('paymentCount');

        if (periodFill) {
            periodFill.style.left = `${Number(periodStart.value) / PERIOD_MAX_MONTHS * 100}%`;
            periodFill.style.right = `${100 - Number(periodEnd.value) / PERIOD_MAX_MONTHS * 100}%`;
        }

        const dates = getInvestmentDates(this.getSettings());
        const formatMonth = (date: Date) => date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
        if (periodDates) {
            periodDates.textContent = dates.length
                ? `${formatMonth(dates[0])} → ${formatMonth(dates[dates.length - 1])}`
                : '';
        }
        if (paymentCount) {
            paymentCount.textContent = `${dates.length} payment${dates.length === 1 ? '' : 's'}`;
        }
    }

    private async calculate(settings: DcaSettings): Promise<DcaResult> {
        const response = await fetch(dataUrl(`history/${this.selectedCoinId}.csv`));
        const csvText = await response.text();
        const history = csvText.split('\n')
            .slice(1) // Skip header row
            .filter(line => line.trim()) // Remove empty lines
            .map(line => {
                const [date, marketCap, open] = line.split(',');
                return {
                    date: new Date(date),
                    marketCap: Number(marketCap),
                    open: Number(open)
                };
            })
            .sort((a, b) => a.date.getTime() - b.date.getTime()); // Sort by date ascending

        const purchaseHistory: DcaResult['purchaseHistory'] = [];
        const postAccumulationHistory: DcaResult['postAccumulationHistory'] = []; // Initialize
        let totalInvested = 0;
        let totalCoinAmount = 0;

        // The full period runs from the first payment until today
        const fullPeriodStartDate = settings.startingDate;
        const fullPeriodEndDate = new Date(); // Today

        // Filter history for the entire period
        const fullHistoryRange = history.filter(entry => entry.date >= fullPeriodStartDate);
        if (fullHistoryRange.length === 0) {
             // Handle case with no data in the selected range
             // console.warn("No historical data found for the selected period."); // Keep console clean
             return {
                 totalInvested: 0, totalValue: 0, percentChange: 0, coinAmount: 0,
                 totalTax: 0, valueWithoutTax: 0, taxEvents: [],
                 startDate: fullPeriodStartDate, endDate: fullPeriodEndDate, purchaseHistory: [], postAccumulationHistory: []
             };
        }


        // Calculate the accumulation end date based on accumulateFor
        const accumulationEndDate = new Date(settings.startingDate);
        accumulationEndDate.setMonth(accumulationEndDate.getMonth() + settings.accumulateFor);

        // Generate target investment dates based on frequency, but only up to accumulationEndDate
        const targetInvestmentDates = getInvestmentDates(settings);


        // --- Investment Calculation Phase (within accumulation period) ---
        
        // For each target investment date, find the closest available price within the full history range
        for (const targetDate of targetInvestmentDates) {
            // Find the closest available date for price in the full history
            let closestEntry = fullHistoryRange[0];
            let minTimeDiff = Infinity;

            for (const entry of fullHistoryRange) {
                // Only consider history points up to or slightly past the target date for investment price
                if (entry.date >= targetDate) { 
                    const timeDiff = Math.abs(entry.date.getTime() - targetDate.getTime());
                    if (timeDiff < minTimeDiff) {
                        minTimeDiff = timeDiff;
                        closestEntry = entry;
                    }
                    // Optimization: Since history is sorted, once we pass the target date significantly, we can stop searching closer
                    if (entry.date > targetDate && timeDiff > minTimeDiff) break; 
                } else if (entry === fullHistoryRange[fullHistoryRange.length - 1]) {
                     // If we reach the end and haven't found a date >= target, use the last one
                     closestEntry = entry;
                }
            }
            
            // Ensure we found a valid entry
            if (!closestEntry) continue; 

            const price = closestEntry.open;
            const investment = settings.purchaseAmount;
            const coinAmount = investment / price;

            totalInvested += investment;
            totalCoinAmount += coinAmount;

            purchaseHistory.push({
                date: targetDate, // Use the intended investment date
                price,
                investment,
                coinAmount
            });
        }

        // --- Post-Accumulation Phase ---

        // Find the index of the first history entry after the accumulation period ended
        const firstPostAccumulationIndex = fullHistoryRange.findIndex(entry => entry.date > accumulationEndDate);

        if (firstPostAccumulationIndex !== -1) {
            // Add remaining history points after accumulation ends
            for (let i = firstPostAccumulationIndex; i < fullHistoryRange.length; i++) {
                const entry = fullHistoryRange[i];
                postAccumulationHistory.push({
                    date: entry.date,
                    price: entry.open
                });
            }
        }


        // --- Yearly Tax on Unrealized Gains ---

        // On each Dec 31 the year's gain (value change minus new contributions) is taxed,
        // after offsetting losses carried forward from earlier years. The tax is paid by selling coins.
        const taxEvents: DcaResult['taxEvents'] = [];
        let totalTax = 0;
        if (settings.taxRate > 0) {
            let coins = 0;
            let purchaseIndex = 0;
            let yearStartValue = 0;
            let yearContributions = 0;
            let lossCarryForward = 0;

            // The last entry is skipped: the current year has not ended yet
            for (let i = 0; i < fullHistoryRange.length - 1; i++) {
                const entry = fullHistoryRange[i];
                while (purchaseIndex < purchaseHistory.length && purchaseHistory[purchaseIndex].date <= entry.date) {
                    coins += purchaseHistory[purchaseIndex].coinAmount;
                    yearContributions += purchaseHistory[purchaseIndex].investment;
                    purchaseIndex++;
                }

                const isYearEnd = entry.date.getUTCFullYear() !== fullHistoryRange[i + 1].date.getUTCFullYear();
                if (!isYearEnd) continue;

                const value = coins * entry.open;
                const gain = value - yearStartValue - yearContributions;
                let tax = 0;
                if (gain < 0) {
                    lossCarryForward -= gain;
                } else {
                    const offset = Math.min(gain, lossCarryForward);
                    lossCarryForward -= offset;
                    tax = (gain - offset) * settings.taxRate;
                }

                if (tax > 0) {
                    const coinsSold = tax / entry.open;
                    coins -= coinsSold;
                    totalTax += tax;
                    taxEvents.push({ date: entry.date, tax, coinsSold });
                }
                yearStartValue = value - tax;
                yearContributions = 0;
            }
        }
        const totalCoinsSold = taxEvents.reduce((sum, e) => sum + e.coinsSold, 0);

        // --- Final Calculation ---

        // Use the last available price from the *entire* filtered history range for final value calculation
        const finalPriceEntry = fullHistoryRange[fullHistoryRange.length - 1];
        const finalPrice = finalPriceEntry.open;
        const coinAmount = totalCoinAmount - totalCoinsSold;
        const totalValue = coinAmount * finalPrice; // Value based on final accumulated coins, after tax
        const percentChange = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested) * 100 : 0; // Avoid division by zero

        return {
            totalInvested,
            totalValue,
            percentChange,
            coinAmount,
            totalTax,
            valueWithoutTax: totalCoinAmount * finalPrice,
            taxEvents,
            startDate: fullPeriodStartDate, // Use the actual start date of the whole period
            endDate: finalPriceEntry.date, // Use the date of the last data point in the full range
            purchaseHistory,
            postAccumulationHistory // Include the post-accumulation data
        };
    } // Correctly close the calculate method

    private updateUI(result: DcaResult) { // Start the updateUI method definition
        const totalInvested = document.getElementById('totalInvested');
        const totalValue = document.getElementById('totalValue');
        const percentChange = document.getElementById('percentChange');

        if (totalInvested) totalInvested.textContent = `$${result.totalInvested.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        if (totalValue) totalValue.textContent = `$${result.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        if (percentChange) {
            const sign = result.percentChange >= 0 ? '+' : '';
            percentChange.textContent = `${sign}${result.percentChange.toFixed(2)}%`;
            percentChange.className = `result-value ${result.percentChange >= 0 ? 'positive' : 'negative'}`;
        }

        const taxPaidCard = document.getElementById('taxPaidCard');
        const taxPaid = document.getElementById('taxPaid');
        const valueWithoutTax = document.getElementById('valueWithoutTax');
        const taxEnabled = this.getSettings().taxRate > 0;
        if (taxPaidCard) taxPaidCard.hidden = !taxEnabled;
        if (taxPaid) taxPaid.textContent = `$${result.totalTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        if (valueWithoutTax) {
            const lostToTax = result.valueWithoutTax - result.totalValue;
            const lostPercent = result.valueWithoutTax > 0 ? (lostToTax / result.valueWithoutTax) * 100 : 0;
            valueWithoutTax.textContent =
                `Without tax: $${result.valueWithoutTax.toLocaleString(undefined, { maximumFractionDigits: 2 })} ` +
                `(${lostPercent.toFixed(1)}% less value)`;
        }

        // Payment count depends on the purchase frequency too
        this.updatePeriodSummary();
        this.updateChart(result, taxEnabled);
    }

    private updateChart(result: DcaResult, showValueWithoutTax: boolean) {
        if (!this.chart) return;

        // Coins sold to pay tax up to and including the given date
        const coinsSoldBy = (date: Date) =>
            result.taxEvents.reduce((sum, e) => e.date <= date ? sum + e.coinsSold : sum, 0);

        // Calculate cumulative invested amount and portfolio value over time
        let cumulativeInvested = 0;
        let cumulativeCoins = 0;
        const chartData: TimeDataPoint[] = [];
        const chartDataWithoutTax: TimeDataPoint[] = [];
        result.purchaseHistory.forEach(entry => {
            cumulativeInvested += entry.investment;
            cumulativeCoins += entry.coinAmount;
            chartData.push({ x: entry.date, y: (cumulativeCoins - coinsSoldBy(entry.date)) * entry.price });
            chartDataWithoutTax.push({ x: entry.date, y: cumulativeCoins * entry.price });
        });

        // Process post-accumulation history: no more purchases, only tax sales
        result.postAccumulationHistory.forEach(entry => {
            chartData.push({ x: entry.date, y: (cumulativeCoins - coinsSoldBy(entry.date)) * entry.price });
            chartDataWithoutTax.push({ x: entry.date, y: cumulativeCoins * entry.price });
        });

        // Combine the data points and ensure we have at least 2 points for each dataset
        const fullChartData = chartData;
        
        // Ensure we have at least 2 points for each dataset
        if (fullChartData.length < 2) {
            // If we only have one point, add a dummy point to prevent curve interpolation issues
            const singlePoint = fullChartData[0];
            if (singlePoint) {
                const dummyDate = new Date(singlePoint.x);
                dummyDate.setDate(dummyDate.getDate() + 1);
                fullChartData.push({
                    x: dummyDate,
                    y: singlePoint.y
                });
            }
        }

        // Create invested data showing cumulative investment over time
        let currentInvested = 0;
        const investedData: TimeDataPoint[] = result.purchaseHistory.map(entry => {
            currentInvested += entry.investment;
            return {
                x: entry.date,
                y: currentInvested
            };
        });

        // For post-accumulation period, keep the final investment amount
        const finalInvestment = investedData.length > 0 ? investedData[investedData.length - 1].y : 0;
        const postAccumulationInvestedData: TimeDataPoint[] = result.postAccumulationHistory.map(entry => ({
            x: entry.date,
            y: finalInvestment
        }));

        // Combine investment data
        const fullInvestedData = [...investedData, ...postAccumulationInvestedData];

        // Set portfolio value color based on final percent change
        const isProfitable = result.percentChange > 0;
        this.chart.data.datasets[0].borderColor = isProfitable ? '#28a745' : '#dc3545';
        this.chart.data.datasets[0].backgroundColor = isProfitable ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)';

        this.chart.data.datasets[0].data = fullChartData;
        this.chart.data.datasets[1].data = fullInvestedData;
        this.chart.data.datasets[2].data = showValueWithoutTax ? chartDataWithoutTax : [];
        this.chart.update();
    }

    public async updateCalculation() {
        const settings = this.getSettings();
        const result = await this.calculate(settings);
        this.updateUI(result);
    }

    public async selectCoin(coinId: string) {
        this.selectedCoinId = coinId;
        const selectedCoinName = document.getElementById('selectedCoinName');
        if (selectedCoinName) {
            const coin = this.coin_markets.find(c => c.id === coinId);
            if (coin) {
                selectedCoinName.textContent = `$${coin.symbol.toUpperCase()}`;
            }
        }

        // Clear the search input and reset the filter
        const searchInput = document.querySelector('.coin-search') as HTMLInputElement;
        if (searchInput) {
            searchInput.value = '';
            // Trigger the input event to update the component
            searchInput.dispatchEvent(new Event('input'));
        }
    }

    public setCoinMarkets(markets: any[]) {
        this.coin_markets = markets;
    }

    public getSelectedCoinId(): string {
        return this.selectedCoinId;
    }
}


// Initialize the calculator when the DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
    // Update URL when settings change
    const updateUrlWithSettings = () => {
        const settings = calculator.getSettings();
        const query: any = {};
        
        if (settings.purchaseAmount !== purchaseAmount_default) {
            query.amount = settings.purchaseAmount;
        }
        if (settings.repeatPurchase !== purchaseFrequency_default) {
            query.frequency = settings.repeatPurchase;
        }
        if (settings.accumulateFor !== accumulateFor_default) {
            query.accumulate = settings.accumulateFor;
        }
        if (settings.startingFrom !== startingFrom_default) {
            query.starting = settings.startingFrom;
        }
        if (calculator.getSelectedCoinId() !== coinId_default) {
            query.coin = calculator.getSelectedCoinId();
        }
        if (settings.taxRate > 0) {
            query.tax = Number((settings.taxRate * 100).toFixed(2));
        }

        router.navigate('/', query);
    };

    const calculator = new DcaCalculator(updateUrlWithSettings);
    
    // Load initial data
    const { coin_markets, latest_update } = await loadAllCoinsData();
    displayLatestUpdate(latest_update);

    // Set coin markets in calculator
    calculator.setCoinMarkets(coin_markets);

    // Initialize share button
    initShareButton(document.querySelector('.share-button'));

    // Initialize available coins component
    const availableCoinsComponent = new AvailableCoinsComponent(
        document.querySelector('.available-coins-container')!,
        new Set(), // Empty set since we don't track selected coins in DCA
        coin_markets,
        (coinId) => {
            calculator.selectCoin(coinId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    );

    // Handle route changes
    router.on('/', async (params, query) => {
        const settings: any = {};
        
        if (query.coin) {
            const coinId = Array.isArray(query.coin) ? query.coin[0] : query.coin;
            settings.coinId = coinId;
        }
        if (query.amount) {
            const amount = Number(Array.isArray(query.amount) ? query.amount[0] : query.amount);
            settings.purchaseAmount = amount;
        }
        if (query.frequency) {
            const freq = Array.isArray(query.frequency) ? query.frequency[0] : query.frequency;
            if (['weekly', 'monthly', 'quarterly'].includes(freq.toLowerCase())) {
                settings.repeatPurchase = freq.toLowerCase() as 'weekly' | 'monthly' | 'quarterly';
            }
        }
        if (query.accumulate) {
            const accumulate = Number(Array.isArray(query.accumulate) ? query.accumulate[0] : query.accumulate);
            settings.accumulateFor = accumulate;
        }
        if (query.starting) {
            const starting = Number(Array.isArray(query.starting) ? query.starting[0] : query.starting);
            settings.startingFrom = starting;
        }
        if (query.tax) {
            const tax = Number(Array.isArray(query.tax) ? query.tax[0] : query.tax);
            if (tax > 0 && tax <= 100) {
                settings.taxPercent = tax;
            }
        }

        // Apply all settings at once
        calculator.updateSettings(settings);
        await calculator.updateCalculation();
    });

    // Trigger initial route handler to apply any query parameters from hash
    const initialHash = window.location.hash;
    router.navigate('/', router.getQuery());
    if (window.location.hash === initialHash) {
        // Hash unchanged, so no hashchange event fires: run the route handler ourselves
        window.dispatchEvent(new HashChangeEvent('hashchange'));
    }

    // Update the selectCoin method to update URL
    const originalSelectCoin = calculator.selectCoin;
    calculator.selectCoin = async (coinId: string) => { // Make it async again to match original signature
        await originalSelectCoin.call(calculator, coinId); // Update internal state first (await original async method)
        updateUrlWithSettings(); // Then update the URL
    };

    // Initial calculation needs to happen after settings are applied by router
    // The router.navigate call below will trigger the initial calculation via the handler
});
